/**
 * InfoModal — a reusable ℹ️ help button that opens a small modal with a title and a
 * concise, friendly guide. Dropped next to each major section/page heading so anyone
 * (technical or not) can learn what a section is for and how to use it.
 *
 * Usage:
 *   <InfoModal title="Command Center" body={["First line.", "Second line."]} />
 *   <InfoModal title="Chat"><p>Custom JSX guide…</p></InfoModal>
 *
 * `body` accepts an array of paragraph strings (rendered as <p>) for the common case;
 * pass children for richer content.
 */
import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';

export interface InfoModalProps {
  title: string;
  /** Concise guide as paragraph strings. Ignored if `children` is provided. */
  body?: string[];
  children?: React.ReactNode;
  /** Optional size of the trigger button. */
  size?: 'sm' | 'md';
  /** Optional accessible label for the trigger. */
  label?: string;
}

const InfoModal: React.FC<InfoModalProps> = ({ title, body, children, size = 'md', label }) => {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);

  const dim = size === 'sm' ? 22 : 26;

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        title={label || `What is "${title}"?`}
        aria-label={label || `Guide: ${title}`}
        style={{
          width: dim,
          height: dim,
          minHeight: dim,
          flex: '0 0 auto',
          borderRadius: '50%',
          border: `1px solid ${theme.border}`,
          backgroundColor: theme.surface,
          color: theme.textSecondary,
          cursor: 'pointer',
          fontSize: size === 'sm' ? 12 : 14,
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
          padding: 0,
        }}
      >
        ℹ
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            zIndex: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            style={{
              width: 'min(480px, 100%)',
              maxHeight: '80vh',
              overflowY: 'auto',
              backgroundColor: theme.surface,
              border: `1px solid ${theme.border}`,
              borderRadius: 12,
              boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
              padding: 22,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                marginBottom: 12,
              }}
            >
              <h3 style={{ margin: 0, color: theme.text, fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>ℹ️</span> {title}
              </h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                style={{
                  background: 'none',
                  border: 'none',
                  color: theme.textSecondary,
                  cursor: 'pointer',
                  fontSize: 20,
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>
            <div style={{ color: theme.text, fontSize: 14, lineHeight: 1.6 }}>
              {children
                ? children
                : (body || []).map((p, i) => (
                    <p key={i} style={{ margin: i === 0 ? '0 0 10px' : '0 0 10px' }}>
                      {p}
                    </p>
                  ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InfoModal;
